import type {
  ColumnMeta,
  FkEdge,
  SchemaSnapshot,
  TableMeta,
} from '@/core/engines/intellisenseTypes';
import type {
  SqliteSchemaColumn,
  SqliteSchemaObject,
  SqliteSchemaSnapshot,
} from '@/features/connections/services/language-services/sqliteSchemaTypes';
import { getCachedSqliteSchema } from '@/features/connections/services/language-services/sqliteSchemaCache';
import { classifyJsonColumn } from '@/features/sql-resolver/json/classify/classifyJsonColumn';
import { detectSqliteJsonValidColumns } from '@/features/sql-resolver/json/classify/checkIsJsonDetection';

function projectColumn(
  c: SqliteSchemaColumn,
  isJsonChecked: boolean,
  isUnique: boolean,
): ColumnMeta {
  const classification = classifyJsonColumn(c.dataType, 'sqlite', {
    hasIsJsonCheck: isJsonChecked,
  });
  const base: ColumnMeta = {
    name: c.name,
    dataType: c.dataType,
    nullable: c.isNullable,
    isPrimaryKey: c.isPrimaryKey,
    ...(isUnique ? { isUnique: true } : {}),
    ordinal: c.ordinalPosition,
  };
  return classification.kind === 'not-json'
    ? base
    : { ...base, jsonClassification: classification };
}

/**
 * Project ONE native relation entry into its bus-shaped `TableMeta`. Exported
 * for the tiered snapshot load (SLICES.md § S7 items 1 + 8): phase 2 walks the
 * per-table PRAGMAs in batches and re-projects only the relations a batch
 * touched, instead of re-running the whole snapshot projection per batch.
 */
export function projectSqliteTableEntry(o: SqliteSchemaObject, database: string): TableMeta {
  const checkSet = o.sql
    ? new Set<string>(detectSqliteJsonValidColumns(o.sql))
    : new Set<string>();
  // Single-column UNIQUE indexes only (incl. the sqlite_autoindex_* rows a
  // column-level UNIQUE constraint creates) — a composite-unique member
  // alone is not a valid ON CONFLICT target.
  const uniqueSingle = new Set<string>(
    o.indexes
      .filter((i) => i.isUnique && i.columns.length === 1)
      .map((i) => i.columns[0]!.toLowerCase()),
  );
  return {
    name: o.name,
    // An object in an ATTACHed database carries that attach alias; everything
    // else belongs to the connection's own database. `database` is the field
    // the SQLite resolver matches a qualifier on (`aux_db.tbl`, `main.tbl`) —
    // it has no `schema` notion of its own.
    database: o.schema ?? database,
    kind: o.type === 'view' ? 'view' : 'table',
    columns: o.columns.map((c) =>
      projectColumn(c, checkSet.has(c.name), uniqueSingle.has(c.name.toLowerCase())),
    ),
    // Already collected by `PRAGMA index_list` / `index_info` — no extra
    // round-trip. Drives the index-NAME slot of `DROP INDEX ▮`. The
    // `sqlite_autoindex_*` rows a UNIQUE/PK constraint creates are included:
    // SQLite refuses to drop them, but the writer who typed `DROP INDEX
    // sqlite_autoindex` deserves to see the name that exists rather than an
    // empty widget.
    indexes: o.indexes.map((i) => ({
      name: i.name,
      columns: i.columns,
      isUnique: i.isUnique,
      isPrimary: i.isPrimary === true,
    })),
  };
}

/**
 * Group SQLite's per-row FK records (one row per (id, column)) into
 * `FkEdge`s by `id`. Composite FKs surface as a single edge with
 * `fromColumns.length === toColumns.length > 1`. SQLite's `PRAGMA foreign_key_list`
 * returns rows in (id, seq) order so collecting in iteration order preserves
 * column ordering on both sides of the edge.
 */
const projectTable = projectSqliteTableEntry;

export function projectSqliteForeignKeys(table: SqliteSchemaObject): FkEdge[] {
  const byId = new Map<
    number,
    { fromColumns: string[]; toTable: string; toColumns: string[] }
  >();
  for (const fk of table.foreignKeys) {
    const existing = byId.get(fk.id);
    if (existing) {
      existing.fromColumns.push(fk.from);
      existing.toColumns.push(fk.to);
    } else {
      byId.set(fk.id, {
        fromColumns: [fk.from],
        toTable: fk.referencedTable,
        toColumns: [fk.to],
      });
    }
  }
  return Array.from(byId.values()).map((edge) => ({
    fromTable: table.name,
    fromColumns: edge.fromColumns,
    toTable: edge.toTable,
    toColumns: edge.toColumns,
  }));
}

/**
 * Pure projection: native SQLite snapshot → bus `SchemaSnapshot`. SQLite
 * has no user-defined routines surfaced through this cache (signature help
 * uses a static built-in table), so `routines` is always empty.
 */
export function projectSqliteSnapshot(native: SqliteSchemaSnapshot): SchemaSnapshot {
  const tables: TableMeta[] = [];
  for (const t of native.tables) tables.push(projectTable(t, native.database));
  for (const v of native.views) tables.push(projectTable(v, native.database));

  const foreignKeys: FkEdge[] = [];
  for (const t of native.tables) foreignKeys.push(...projectSqliteForeignKeys(t));

  // `PRAGMA database_list` inventory, renamed onto the bus's
  // `{ alias, path }` shape (the same one `ScriptScope.attachedDatabases`
  // uses) so the resolver can merge it with in-buffer ATTACH statements.
  // Omitted when nothing is attached so the field stays undefined for the
  // overwhelmingly common single-file connection.
  const attachedDatabases = (native.attachedDatabases ?? []).map((db) => ({
    alias: db.name,
    path: db.file,
  }));

  return {
    engineId: 'sqlite',
    connectionId: native.connectionId,
    database: native.database,
    schemas: [],
    tables,
    routines: [],
    foreignKeys,
    serverVersion: native.serverVersion || undefined,
    ...(attachedDatabases.length > 0 ? { attachedDatabases } : {}),
    builtAt: native.builtAt,
  };
}

export function buildSnapshot(
  connectionId: string,
  database: string,
): SchemaSnapshot | null {
  const native = getCachedSqliteSchema(connectionId);
  if (!native || native.database !== database) return null;
  return projectSqliteSnapshot(native);
}
